/**
 * Workforce UI Store
 *
 * Manages ONLY client-side UI state for the workforce module.
 * Server data is handled by TanStack Query hooks in @/hooks/useWorkforce.ts
 *
 * This store contains:
 * - View preferences (grid/list modes)
 * - Filter selections
 * - Modal open/close states
 * - Selected items for bulk operations
 * - Sorting preferences
 *
 * DO NOT store server data here (employees, departments, roles, etc.)
 *
 * @see packages/web-apps/monutrack/docs/ARCHITECTURE_PATTERNS.md
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// ============================================================================
// TYPES
// ============================================================================

export type EmployeeViewMode = 'grid' | 'list' | 'hierarchy'
export type EmployeeStatusFilter = 'all' | 'ACTIVE' | 'INACTIVE' | 'TERMINATED'

interface EmployeeFilters {
  status: EmployeeStatusFilter
  departmentId?: string
  roleId?: string
  search?: string
}

interface SortConfig {
  sortBy: string
  sortOrder: 'asc' | 'desc'
}

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface WorkforceUIStore {
  // ================================
  // EMPLOYEES VIEW STATE
  // ================================

  /** Current view mode for employees page */
  employeesViewMode: EmployeeViewMode

  /** Set employees view mode */
  setEmployeesViewMode: (mode: EmployeeViewMode) => void

  /** Current filter selections for employees */
  employeesFilters: EmployeeFilters

  /** Update employees filters */
  setEmployeesFilters: (filters: Partial<EmployeeFilters>) => void

  /** Reset employees filters to defaults */
  resetEmployeesFilters: () => void

  /** Current sort configuration for employees */
  employeesSort: SortConfig

  /** Set employees sort configuration */
  setEmployeesSort: (sort: SortConfig) => void

  // ================================
  // BULK OPERATIONS STATE
  // ================================

  /** Selected employee IDs for bulk operations */
  selectedEmployeeIds: string[]

  /** Toggle employee selection */
  toggleEmployeeSelection: (employeeId: string) => void

  /** Select all employees */
  selectAllEmployees: (employeeIds: string[]) => void

  /** Clear all selections */
  clearEmployeeSelections: () => void

  /** Check if employee is selected */
  isEmployeeSelected: (employeeId: string) => boolean

  // ================================
  // MODAL STATES
  // ================================

  /** Create employee modal open state */
  isCreateEmployeeModalOpen: boolean

  /** Open create employee modal */
  openCreateEmployeeModal: () => void

  /** Close create employee modal */
  closeCreateEmployeeModal: () => void

  /** Employee ID for edit modal */
  editEmployeeId: string | null

  /** Open edit employee modal */
  openEditEmployeeModal: (employeeId: string) => void

  /** Close edit employee modal */
  closeEditEmployeeModal: () => void

  /** Bulk operation modal open state */
  isBulkOperationModalOpen: boolean

  /** Current bulk operation type */
  bulkOperationType: 'status' | 'department' | 'manager' | null

  /** Open bulk operation modal */
  openBulkOperationModal: (operationType: 'status' | 'department' | 'manager') => void

  /** Close bulk operation modal */
  closeBulkOperationModal: () => void

  /** Create department modal open state */
  isCreateDepartmentModalOpen: boolean

  /** Open create department modal */
  openCreateDepartmentModal: () => void

  /** Close create department modal */
  closeCreateDepartmentModal: () => void

  /** Create role modal open state */
  isCreateRoleModalOpen: boolean

  /** Open create role modal */
  openCreateRoleModal: () => void

  /** Close create role modal */
  closeCreateRoleModal: () => void

  // ================================
  // PAYROLL STATE
  // ================================

  /** Selected period for payroll (YYYY-MM format) */
  selectedPayrollPeriod: string

  /** Set selected payroll period */
  setSelectedPayrollPeriod: (period: string) => void

  // ================================
  // UTILITY
  // ================================

  /** Reset all UI state to defaults */
  reset: () => void
}

// ============================================================================
// DEFAULT STATE
// ============================================================================

const defaultEmployeesFilters: EmployeeFilters = {
  status: 'ACTIVE',
  departmentId: undefined,
  roleId: undefined,
  search: undefined,
}

const defaultEmployeesSort: SortConfig = {
  sortBy: 'personalInfo.name',
  sortOrder: 'asc',
}

const getCurrentYearMonth = (): string => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

const initialState = {
  // Employees view
  employeesViewMode: 'list' as const,
  employeesFilters: defaultEmployeesFilters,
  employeesSort: defaultEmployeesSort,

  // Bulk operations
  selectedEmployeeIds: [],

  // Modals
  isCreateEmployeeModalOpen: false,
  editEmployeeId: null,
  isBulkOperationModalOpen: false,
  bulkOperationType: null,
  isCreateDepartmentModalOpen: false,
  isCreateRoleModalOpen: false,

  // Payroll
  selectedPayrollPeriod: getCurrentYearMonth(),
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

/**
 * Workforce UI Store
 *
 * Persisted to localStorage so preferences survive page refreshes.
 * Modal states and bulk selections are NOT persisted (reset on refresh).
 */
export const useWorkforceUIStore = create<WorkforceUIStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ================================
      // EMPLOYEES VIEW STATE
      // ================================

      setEmployeesViewMode: (mode) =>
        set({ employeesViewMode: mode }),

      setEmployeesFilters: (filters) =>
        set((state) => ({
          employeesFilters: {
            ...state.employeesFilters,
            ...filters,
          },
        })),

      resetEmployeesFilters: () =>
        set({ employeesFilters: defaultEmployeesFilters }),

      setEmployeesSort: (sort) =>
        set({ employeesSort: sort }),

      // ================================
      // BULK OPERATIONS STATE
      // ================================

      toggleEmployeeSelection: (employeeId) =>
        set((state) => ({
          selectedEmployeeIds: state.selectedEmployeeIds.includes(employeeId)
            ? state.selectedEmployeeIds.filter((id) => id !== employeeId)
            : [...state.selectedEmployeeIds, employeeId],
        })),

      selectAllEmployees: (employeeIds) =>
        set({ selectedEmployeeIds: employeeIds }),

      clearEmployeeSelections: () =>
        set({ selectedEmployeeIds: [] }),

      isEmployeeSelected: (employeeId) =>
        get().selectedEmployeeIds.includes(employeeId),

      // ================================
      // MODAL STATES
      // ================================

      openCreateEmployeeModal: () =>
        set({ isCreateEmployeeModalOpen: true }),

      closeCreateEmployeeModal: () =>
        set({ isCreateEmployeeModalOpen: false }),

      openEditEmployeeModal: (employeeId) =>
        set({ editEmployeeId: employeeId }),

      closeEditEmployeeModal: () =>
        set({ editEmployeeId: null }),

      openBulkOperationModal: (operationType) =>
        set({
          isBulkOperationModalOpen: true,
          bulkOperationType: operationType,
        }),

      closeBulkOperationModal: () =>
        set({
          isBulkOperationModalOpen: false,
          bulkOperationType: null,
        }),

      openCreateDepartmentModal: () =>
        set({ isCreateDepartmentModalOpen: true }),

      closeCreateDepartmentModal: () =>
        set({ isCreateDepartmentModalOpen: false }),

      openCreateRoleModal: () =>
        set({ isCreateRoleModalOpen: true }),

      closeCreateRoleModal: () =>
        set({ isCreateRoleModalOpen: false }),

      // ================================
      // PAYROLL STATE
      // ================================

      setSelectedPayrollPeriod: (period) =>
        set({ selectedPayrollPeriod: period }),

      // ================================
      // UTILITY
      // ================================

      reset: () => set(initialState),
    }),
    {
      name: 'workforce-ui-store', // localStorage key

      // Only persist non-ephemeral state
      partialize: (state) => ({
        employeesViewMode: state.employeesViewMode,
        employeesFilters: state.employeesFilters,
        employeesSort: state.employeesSort,
        selectedPayrollPeriod: state.selectedPayrollPeriod,
      }),
    }
  )
)
